import React, {
	lazy,
	Suspense,
	useCallback,
	useEffect,
	useMemo,
	useState
} from 'react';
import {
	Divider,
	Loader,
	Segment
} from 'semantic-ui-react';

import { getAgency } from '../../../api/nora/agencies';
import { createQuickformSubmission, getDocument, getQuickForms } from '../../../api/nora/submissions';

import { useAuthState } from '../../../contexts/AuthContext';
import {
	useAgencyState,
	useAgencyAction
} from '../../../contexts/AgenciesContext';
import {
	useSubmissionAction,
	useSubmissionState
} from '../../../contexts/SubmissionsContext';
import { useUIAction } from '../../../contexts/UIContext';

import DateCell from '../../Table/Cells/DateCell';
import dateFilterFn from '../../Table/filterFunctions/dateFilterFn';
import DocumentModal from '../../DocumentModal';
import HelpModal from '../../Shared/HelpModal';
import MultiSelectColumnFilter from '../../Table/ColumnFilters/MultiSelectColumnFilter';
import multiSelectFilterFn from '../../Table/filterFunctions/multiSelectFilterFn';
import multiWordFilterFn from '../../Table/filterFunctions/multiWordFilterFn';
import PermissionsWrapper from '../../PermissionsWrapper';
import SubmittedMobileColumn from './SubmittedMobileColumn';
import Table from '../../Table';
import TableRowActions from '../../Table/Cells/TableRowActions';
import ToDoMobileColumn from './ToDoMobileColumn';

import styles from '../agency-portal.module.css';
import { getSubmissionReport } from '../../../api/nora/reports';
import MemoMobileColumn from './MemoMobileColumn';

const ToDo = () => {
	const { user } = useAuthState();
	const { agency } = useAgencyState();
	const { setAgency } = useAgencyAction();
	const { document } = useSubmissionState();
	const { setDocument } = useSubmissionAction();
	const {
		setApiErrorAlert,
		setInfobarLoader,
		setScreenLoader
	} = useUIAction();

	const [ documentModal, setDocumentModal ] = useState( false );
	const [ retreivingQuickForms, setRetrievingQuickForms ] = useState( true );
	const [ quickforms, setQuickforms ] = useState( [] );
	const [ width, setWidth ] = useState( window.innerWidth );

	useEffect( () => {
		const handleResize = () => setWidth( window.innerWidth );

		window.addEventListener( 'resize', handleResize );

		return () => window.removeEventListener( 'resize', handleResize );
	}, [] );

	useEffect( () => {
		( async () => {
			setRetrievingQuickForms( true );

			try {
				const response = await getQuickForms();

				setQuickforms( response );
			} catch ( error ) {
				setApiErrorAlert( error );
			}

			setRetrievingQuickForms( false );
		} )();
	}, [ setApiErrorAlert ] );

	const submissions = useMemo(
		() => agency.programs
			.reduce( ( acc, { name, submissions } ) => {
				const updatedSubmissions = submissions.map( submission => ( { ...submission, program: name } ) );

				return [ ...acc, ...updatedSubmissions ];
			}, [] )
			.sort( ( a, b ) => new Date( b.dueDate ) - new Date( a.dueDate ) ),
		[ agency ]
	);

	const memos = useMemo(
		() => submissions
			.filter( ( { type } ) => type === 'Program Memo' )
			.sort( ( a, b ) => new Date( b.createdOn ) - new Date( a.createdOn ) ),
		[ submissions ]
	);
	const submitted = useMemo(
		() => submissions
			.filter( ( { submissionStatus, type } ) => ( submissionStatus.id === 30 || submissionStatus.id === 31 ) && type !== 'Program Memo' )
			.sort( ( a, b ) => new Date( b.submissionDate ) - new Date( a.submissionDate ) ),
		[ submissions ]
	);
	const unsubmitted = useMemo(
		() => submissions
			.filter( ( { submissionStatus, type } ) => submissionStatus.id !== 30 && submissionStatus.id !== 31 && type !== 'Program Memo' )
			.sort( ( a, b ) => new Date( b.dueDate ) - new Date( a.dueDate ) ),
		[ submissions ]
	);

	/** *** This is a temporary way to refresh submission results until the submission endpoint exists *****/
	const retrieveAgency = useCallback( async () => {
		setScreenLoader( 'Retrieving Agency Info' );

		try {
			const userAgency = await getAgency( user.portal.id );

			setAgency( userAgency );
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setScreenLoader( '' );
	}, [
		setAgency,
		setApiErrorAlert,
		setScreenLoader,
		user.portal.id
	] );

	const closeDocumentModal = useCallback( async () => {
		setDocumentModal( false );
		setDocument( null );
		await retrieveAgency();
	}, [
		retrieveAgency,
		setDocument
	] );

	const viewSubmission = useCallback( async id => {
		try {
			setScreenLoader( true );
			setInfobarLoader( 'Retrieving Document...' );
			const doc = await getDocument( id );

			setDocument( doc );
			setDocumentModal( true );
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setScreenLoader( false );
		setInfobarLoader( '' );
	}, [
		setApiErrorAlert,
		setDocument,
		setInfobarLoader,
		setScreenLoader
	] );

	const mapQuickForms = useCallback( () => quickforms.map( form => {
		const Icon = lazy( () => import( `../QuickformIcons/${ form.icon }` ).catch( () => ( { default: null } ) ) );
		const handleClick = async () => {
			setScreenLoader( 'Generating Submission...' );

			try {
				const quickformId = await createQuickformSubmission( form.id );

				await viewSubmission( quickformId );
			} catch ( error ) {
				setApiErrorAlert( error );
			}

			setScreenLoader( '' );
		};

		return (
			<Segment
				className={ styles.quickForm }
				key={ form.id }
				onClick={ handleClick }
				style={ { cursor: 'pointer' } }
			>
				<Suspense fallback={ <div style={{ width: '32px', height: '32px' }} />}>
					<Icon
						width={ 32 }
						height={ 32 }
					/>
				</Suspense>
				<h3
					style={ {
						color: 'var(--main)',
						textAlign: 'center'
					} }
				>
					{ form.description }
				</h3>
			</Segment>
		);
	} ), [
		quickforms,
		setApiErrorAlert,
		setScreenLoader,
		viewSubmission
	] );

	// Data Table
	const dueDateCell = useCallback( ( { row } ) => {
		if ( row.original.dueDate === '0001-01-01T00:00:00' ) {
			return <p>N/A</p>;
		}

		return <DateCell date={ row.original.dueDate } />;
	}, [] );
	const createdOnDateCell = useCallback( ( { row } ) => {
		if ( row.original.createdOn === '0001-01-01T00:00:00' ) {
			return <p>N/A</p>;
		}

		return <DateCell date={ row.original.createdOn } />;
	}, [] );

	const submissionDateCell = useCallback( ( { row } ) => {
		if ( row.original.submissionDate === '0001-01-01T00:00:00' ) {
			return <p>N/A</p>;
		}

		return <DateCell date={ row.original.submissionDate } />;
	}, [] );

	const toDoMobileColumn = useCallback( ( { row } ) => <ToDoMobileColumn row={row.original} />, [] );

	const memoMobileColumn = useCallback( ( { row } ) => <MemoMobileColumn row={row.original} />, [] );

	const submittedMobileColumn = useCallback( ( { row } ) => <SubmittedMobileColumn row={row.original} />, [] );

	const onView = useCallback( ( { id } ) => {
		viewSubmission( id );
	}, [ viewSubmission ] );

	const onDownload = useCallback( async ( { id } ) => {
		setScreenLoader( 'Retreiving Submission Report...' );

		try {
			await getSubmissionReport( id );
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setScreenLoader( '' );
	}, [
		setApiErrorAlert,
		setScreenLoader
	] );

	const tableRowActionsCell = useCallback( ( { row } ) => {
		const actions = [
			{
				action: onView,
				componentProps: { color: 'pink' },
				icon: 'search',
				permission: true,
				text: 'View Submission'
			}
		];

		return (
			<TableRowActions
				actions={ actions }
				row={ row.original }
			/>
		);
	}, [ onView ] );

	const columns = useMemo( () => [
		{
			accessor: 'name',
			Header: 'Name',
			filter: multiWordFilterFn
		},
		{
			accessor: 'program',
			Header: 'Program',
			filter: multiWordFilterFn
		},
		{
			accessor: 'dueDate',
			Header: 'Due Date',
			filter: dateFilterFn,
			Cell: dueDateCell,
			width: 100,
			minWidth: 100,
			maxWidth: 100
		},
		{
			id: 'submissionStatus',
			accessor: row => `${ row.submissionStatus.name }`,
			Header: 'Status',
			Filter: MultiSelectColumnFilter,
			filter: multiSelectFilterFn,
			width: 100,
			minWidth: 100,
			maxWidth: 100
		},
		{
			id: 'id_actions',
			Cell: tableRowActionsCell,
			Header: 'View',
			disableFilters: true,
			disableResizing: true,
			width: 100,
			minWidth: 100,
			maxWidth: 100
		}
	], [
		dueDateCell,
		tableRowActionsCell
	] );

	const mobileColumns = useMemo( () => [
		{
			id: 'mobile_column',
			accessor: row => `${ row.name }${ row.program }${ row.dueDate }${ row.submissionStatus.name }`,
			Header: 'Submissions',
			filter: multiWordFilterFn,
			Cell: toDoMobileColumn,
			minWidth: 60
		},
		{
			id: 'id_actions',
			Cell: tableRowActionsCell,
			Header: 'Read',
			disableFilters: true,
			disableResizing: true,
			width: 100,
			minWidth: 100,
			maxWidth: 100
		}

	], [
		tableRowActionsCell,
		toDoMobileColumn
	] );

	const memoColumns = useMemo( () => [
		{
			accessor: 'name',
			Header: 'Name',
			filter: multiWordFilterFn
		},
		{
			accessor: 'program',
			Header: 'Program',
			filter: multiWordFilterFn
		},
		{
			accessor: 'createdOn',
			Header: 'Created',
			filter: dateFilterFn,
			Cell: createdOnDateCell,
			width: 100,
			minWidth: 100,
			maxWidth: 100
		},
		{
			id: 'id_actions',
			Cell: tableRowActionsCell,
			Header: 'Read',
			disableFilters: true,
			disableResizing: true,
			width: 100,
			minWidth: 100,
			maxWidth: 100
		}
	], [
		createdOnDateCell,
		tableRowActionsCell
	] );

	const memoMobileColumns = useMemo( () => [
		{
			id: 'mobile_column',
			accessor: row => `${ row.name }${ row.program }${ row.createdOn }`,
			Header: 'Submissions',
			filter: multiWordFilterFn,
			Cell: memoMobileColumn,
			minWidth: 60
		},
		{
			id: 'id_actions',
			Cell: tableRowActionsCell,
			Header: 'View',
			disableFilters: true,
			disableResizing: true,
			width: 100,
			minWidth: 100,
			maxWidth: 100
		}

	], [
		tableRowActionsCell,
		memoMobileColumn
	] );

	const submittedTableRowActionsCell = useCallback( ( { row } ) => {
		const actions = [
			{
				action: onDownload,
				componentProps: { color: 'pink' },
				icon: 'file pdf',
				permission: true,
				text: 'Download Submission'
			}
		];

		return (
			<TableRowActions
				actions={ actions }
				row={ row.original }
			/>
		);
	}, [ onDownload ] );

	const submittedColumns = useMemo( () => [
		{
			accessor: 'name',
			Header: 'Name',
			filter: multiWordFilterFn
		},
		{
			accessor: 'program',
			Header: 'Program',
			filter: multiWordFilterFn
		},
		{
			accessor: 'submissionDate',
			Header: 'Submission Date',
			filter: dateFilterFn,
			Cell: submissionDateCell,
			width: 100,
			minWidth: 100,
			maxWidth: 100
		},
		{
			id: 'submissionStatus',
			accessor: row => `${ row.submissionStatus.name }`,
			Header: 'Status',
			Filter: MultiSelectColumnFilter,
			filter: multiSelectFilterFn,
			width: 100,
			minWidth: 100,
			maxWidth: 100
		},
		{
			id: 'id_actions',
			Cell: submittedTableRowActionsCell,
			Header: 'Download',
			disableFilters: true,
			disableResizing: true,
			width: 100,
			minWidth: 100,
			maxWidth: 100
		}
	], [
		submissionDateCell,
		submittedTableRowActionsCell
	] );

	const submittedMobileColumns = useMemo( () => [
		{
			id: 'mobile_column',
			accessor: row => `${ row.name }${ row.program }${ row.submissionDate }${ row.submissionStatus.name }`,
			Header: 'Submissions',
			filter: multiWordFilterFn,
			Cell: submittedMobileColumn,
			minWidth: 60
		},
		{
			id: 'id_actions',
			Cell: submittedTableRowActionsCell,
			Header: 'Download',
			disableFilters: true,
			disableResizing: true,
			width: 100,
			minWidth: 100,
			maxWidth: 100
		}

	], [
		submittedTableRowActionsCell,
		submittedMobileColumn
	] );

	return (
		<>
			{ memos.length
				? <div className={ styles.container } >
					<header className={ styles.pageHeader }>
						<h2>
						Program Memos
							<HelpModal
								id={ 2 }
								triggerStyles={ { verticalAlign: 'top' } }
							/>
						</h2>
					</header>
					<Segment
						basic
						as="div"
						className={ styles.content }
					>
						<PermissionsWrapper
							alt={ <p>Access denied.</p> }
							permission={ user.permissions.agencyEditPrograms.canExecute }
						>
							{/* { renderTable( visibleToDoEntries, renderToDoPagination ) } */}
							<Table
								columns={ width < 767 ? memoMobileColumns : memoColumns }
								data={ memos }
								showCount={ memos.length > 10 }
								showEmptyRows={ false }
								showPagination={ memos.length > 10 }
								style={ { overflowX: 'auto' } }
							/>
						</PermissionsWrapper>
					</Segment>
				</div>
				: null
			}
			<div className={ styles.container } >
				<header className={ styles.pageHeader }>
					<h2>
						To Do
						<HelpModal
							id={ 2 }
							triggerStyles={ { verticalAlign: 'top' } }
						/>
					</h2>
				</header>
				<Segment
					basic
					as="div"
					className={ styles.content }
				>
					<PermissionsWrapper
						alt={ <p>Access denied.</p> }
						permission={ user.permissions.agencyEditPrograms.canExecute }
					>
						{/* { renderTable( visibleToDoEntries, renderToDoPagination ) } */}
						<Table
							columns={ width < 767 ? mobileColumns : columns }
							data={ unsubmitted }
							showCount={ unsubmitted.length > 10 }
							showEmptyRows={ false }
							showPagination={ unsubmitted.length > 10 }
							style={ { overflowX: 'auto' } }
						/>
					</PermissionsWrapper>
				</Segment>
			</div>
			{ submitted.length
				? <div className={ styles.container } >
					<header className={ styles.pageHeader }>
						<h2>
						Submitted
							<HelpModal
								id={ 3 }
								triggerStyles={ { verticalAlign: 'top' } }
							/>
						</h2>
					</header>
					<Segment
						basic
						as="div"
						className={ styles.content }
					>
						<PermissionsWrapper
							alt={ <p>Access denied.</p> }
							permission={ user.permissions.agencyEditPrograms.canExecute }
						>
							{/* { renderTable( visibleSubmittedEntries, renderSubmittedPagination ) } */}
							<Table
								columns={ width < 767 ? submittedMobileColumns : submittedColumns }
								data={ submitted }
								showCount={ submitted.length > 10 }
								showEmptyRows={ false }
								showPagination={ submitted.length > 10 }
								style={ { overflowX: 'auto' } }
							/>
						</PermissionsWrapper>
					</Segment>
				</div>
				: null
			}
			<div className={ styles.container }>
				<header className={ styles.pageHeader }>
					<h2>
						Quick Forms
						<HelpModal
							id={ 4 }
							triggerStyles={ { verticalAlign: 'top' } }
						/>
					</h2>
				</header>
				<Segment
					basic
					as="div"
					className={ styles.content }
				>
					<div style={ {
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-around',
						flexWrap: 'wrap'
					} }>
						{ retreivingQuickForms ? <div style={{ height: '32px' }}><Loader active /></div> : quickforms.length ? mapQuickForms() : 'No quickforms found.' }
					</div>
				</Segment>
			</div>
			<Divider hidden />
			<DocumentModal
				canSubmit
				onSubmit={ retrieveAgency }
				closeModal={ closeDocumentModal }
				open={ document && documentModal }
			/>
		</>
	);
};

export default ToDo;
